import { useEffect, useState, useCallback } from "react";
import { Eye, XCircle } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUS_BADGE = {
  upcoming: "bg-accent text-accent-foreground",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
  pending_payment: "bg-amber-50 text-amber-700",
};

export default function AdminAppointments() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [clinicId, setClinicId] = useState("");
  const [page, setPage] = useState(1);
  const [doctors, setDoctors] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [viewId, setViewId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    const p = new URLSearchParams({ page: String(page), page_size: "20" });
    if (status) p.set("status", status);
    if (doctorId) p.set("doctor_id", doctorId);
    if (clinicId) p.set("clinic_id", clinicId);
    const { data } = await api.get(`/admin/appointments?${p.toString()}`);
    setItems(data.items); setTotal(data.total);
  }, [status, doctorId, clinicId, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/doctors?page_size=200").then((r) => setDoctors(r.data.items));
    api.get("/admin/clinics?page_size=200").then((r) => setClinics(r.data.items));
  }, []);

  useEffect(() => {
    if (!viewId) return;
    api.get(`/admin/appointments?page_size=1&page=1`).then(() => {});
    // Use existing items in memory for detail (cheaper). Fetch full row from list.
    const found = items.find((a) => a.id === viewId);
    if (found) setViewData(found);
  }, [viewId, items]);

  const cancel = async () => {
    if (!cancelTarget) return;
    try {
      await api.post(`/admin/appointments/${cancelTarget}/cancel`, { reason: reason || "admin_cancellation" });
      toast.success("Appointment cancelled & patient notified");
      setCancelTarget(null); setReason(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <AdminLayout title="Appointments" subtitle={`${total} total`}>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="admin-appts-status" className="w-44 rounded-full"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="upcoming">Upcoming</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="pending_payment">Pending payment</SelectItem>
          </SelectContent>
        </Select>
        <Select value={doctorId || "all"} onValueChange={(v) => { setDoctorId(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="admin-appts-doctor" className="w-56 rounded-full"><SelectValue placeholder="Doctor" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All doctors</SelectItem>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={clinicId || "all"} onValueChange={(v) => { setClinicId(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="admin-appts-clinic" className="w-56 rounded-full"><SelectValue placeholder="Clinic" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All clinics</SelectItem>{clinics.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div data-testid="admin-appts-table" className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left p-3">ID</th><th className="text-left p-3">Patient</th><th className="text-left p-3">Doctor</th>
                <th className="text-left p-3">Mode</th><th className="text-left p-3">Scheduled</th>
                <th className="text-left p-3">Status</th><th className="text-right p-3">Fee</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No appointments</td></tr>
                : items.map((a) => (
                <tr key={a.id} data-testid={`admin-appt-row-${a.id}`} className="border-t border-border hover:bg-secondary/30">
                  <td className="p-3 font-mono text-xs">{a.id}</td>
                  <td className="p-3">{a.patient?.name || a.patient?.phone}</td>
                  <td className="p-3">{a.doctor?.name}</td>
                  <td className="p-3 capitalize">{a.mode}</td>
                  <td className="p-3 text-xs">{a.scheduled_at_local}</td>
                  <td className="p-3"><span className={"text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full " + (STATUS_BADGE[a.status] || "bg-muted text-muted-foreground")}>{a.status}</span></td>
                  <td className="p-3 text-right tabular-nums">₹{a.fee}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" data-testid={`admin-appt-view-${a.id}`} onClick={() => setViewId(a.id)}><Eye className="h-4 w-4" /></Button>
                    {(a.status === "upcoming" || a.status === "pending_payment") && (
                      <Button size="sm" variant="ghost" data-testid={`admin-appt-cancel-${a.id}`} onClick={() => setCancelTarget(a.id)}><XCircle className="h-4 w-4 text-rose-600" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {total > 20 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {Math.ceil(total / 20)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <Dialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setReason(""); } }}>
        <DialogContent data-testid="admin-cancel-modal" className="sm:max-w-md rounded-2xl">
          <DialogHeader><DialogTitle>Cancel appointment</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This will refund the patient and notify both parties. Provide a reason:</p>
          <Input data-testid="admin-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. doctor unavailable" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelTarget(null)} className="rounded-full">Keep</Button>
            <Button data-testid="admin-cancel-confirm" onClick={cancel} className="rounded-full bg-rose-600 hover:bg-rose-600/90 text-white hover:text-white">Cancel appointment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!viewId} onOpenChange={(o) => { if (!o) { setViewId(null); setViewData(null); } }}>
        <SheetContent data-testid="admin-appt-view-drawer" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle>Appointment {viewData?.id}</SheetTitle></SheetHeader>
          {viewData && (
            <div className="space-y-3 mt-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-secondary/40 rounded-xl p-2"><div className="text-xs text-muted-foreground">Patient</div><div className="font-medium">{viewData.patient?.name || viewData.patient?.phone}</div></div>
                <div className="bg-secondary/40 rounded-xl p-2"><div className="text-xs text-muted-foreground">Doctor</div><div className="font-medium">{viewData.doctor?.name}</div></div>
                <div className="bg-secondary/40 rounded-xl p-2"><div className="text-xs text-muted-foreground">Mode</div><div className="font-medium capitalize">{viewData.mode}</div></div>
                <div className="bg-secondary/40 rounded-xl p-2"><div className="text-xs text-muted-foreground">Status</div><div className="font-medium">{viewData.status}</div></div>
                <div className="bg-secondary/40 rounded-xl p-2"><div className="text-xs text-muted-foreground">Fee</div><div className="font-medium">₹{viewData.fee}</div></div>
                <div className="bg-secondary/40 rounded-xl p-2"><div className="text-xs text-muted-foreground">Scheduled</div><div className="font-medium text-xs">{viewData.scheduled_at_local}</div></div>
              </div>
              {viewData.cancel_reason && <div className="bg-rose-50 border border-rose-200 rounded-xl p-2 text-xs"><span className="font-semibold">Cancel reason: </span>{viewData.cancel_reason}</div>}
              {viewData.invoice_id && <div className="text-xs text-muted-foreground">Invoice: <span className="font-mono">{viewData.invoice_id}</span></div>}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
}
